<template lang="pug">
#resident-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 利用者情報修正

    .box-body.register-body
      FormLabel(key-name="利用者ID" :value="resident.id")
      FormInput(v-model="resident.name" key-name="利用者名" name="resident-name" type="text" placeholder="利用者名"
        :maxlength="32" :validation-rules="{ required: true, max: 32 }" :styles="{ width: '340px' }" required)
      FormInput(v-model="resident.kanaName" key-name="利用者名（ふりがな）" name="resident-kana-name" type="text" placeholder="利用者名（ふりがな）"
        :maxlength="32" :validation-rules="{ max: 32, hiraganaAndNumbers: true }" :styles="{ width: '340px' }")
      FormInputGender(key-name="性別" v-model="resident.gender")
      FormInputSplitDate(:year.sync="year" :month.sync="month" :day.sync="day" key-name="生年月日"
        validationDateKeyName="生年月日")
      FormInput(v-model="resident.tel" key-name="電話番号" name="tel" type="text" placeholder="電話番号"
        :maxlength="13" :validation-rules="{ tel: true, max: 13 }" :styles="{ width: '340px' }")
      FormInputSelect(v-model="resident.roomId" key-name="部屋" name="room-id"
        :options="roomOptions" :validation-rules="{ required: true }" :styles="{ 'max-width': '540px' }" required)
      FormInputSelect(v-model="resident.groupId" key-name="利用者グループ" name="group-id"
        :options="residentGroupOptions" :validation-rules="{ required: true }" :styles="{ 'max-width': '540px' }" required)
      FormInput(v-model="resident.insuredNumber" key-name="保険者番号" name="insured-number" type="text" placeholder="保険者番号"
        :maxlength="6" :validation-rules="{ length: 6, max: 6, custom_numeric: true }" :styles="{ width: '180px' }")
      FormInput(v-model="resident.careInsuranceInsuredNumber" key-name="介護保険被保険者番号" name="care-insurance-insured-number"
        type="text" placeholder="介護保険被保険者番号" :maxlength="10" :validation-rules="{ length: 10, max: 10, half_alpha_num: true }" :styles="{ width: '320px' }")
      
      .register-row(style="margin-bottom:0;")
        .register-group
          .register-key
            span.key-name 入所・退所情報
            HelpIcon.d-flex.justify-content-end.align-items-center(popoverId="in-and-out-datetimes-help" title="入所・退所情報について")
              template(v-slot:text)
                div 入所・退所情報は「空床スケジュール」、他事業所連携サービスの「空床情報」に反映されます。
                div ショートステイの複数回利用など、複数の入所・退所情報を登録することもできます。
      .register-row(v-if="inAndOutDatetimes" v-for="(item, index) in inAndOutDatetimes" style="margin-bottom:0;")
        .register-group
          .register-key
            span.key-name {{ index + 1 }}
            button.btn.delete-btn(@click="deleteInAndOutDatetime(index)") 削除
        .in-and-out-datetime
          FormDatetimePicker(v-model="item.inDatetime" :date.sync="item.inDatetimeDate" :time.sync="item.inDatetimeTime"
            placeholder="入所日／入所予定日" key-name="入所日／入所予定日" :name="'in-date-' + index")
          FormTimePicker(v-if="item.inDatetime" v-model="item.pickUpDatetime" :time.sync="item.pickUpDatetimeTime"
            key-name="入所送迎時間" :name="'pick-up-time' + index" placeholder="入所送迎時間")
          FormDatetimePicker(v-model="item.outDatetime" :date.sync="item.outDatetimeDate" :time.sync="item.outDatetimeTime"
            key-name="退所日／退所予定日" :name="'out-date' + index" placeholder="退所日／退所予定日")
          FormTimePicker(v-if="item.outDatetime" v-model="item.dropOffDatetime" :time.sync="item.dropOffDatetimeTime"
            key-name="退所送迎時間" :name="'drop-off-time' + index" placeholder="退所送迎時間")
          FormInputSelect(v-model="item.roomId" key-name="部屋" :name="'room-id-' + index"
            :options="roomOptions" :styles="{ 'max-width': '540px' }")
      button.btn.add-btn.mb-3(@click="addInAndOutDatetime" type="button" style="margin: 0 20px;"
        :class="{ 'disable': _disableAddInAndOutDatetime }" :disabled="_disableAddInAndOutDatetime") 追加

      FormLabel(key-name="家族ユーザー数" :value="resident.familyCount + '人'")
      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(@click="showConfirmUpdateModal()" type="button") 更新
        button.btn.cancel-btn(@click="gotoResidentList()" type="button") キャンセル

  OkCancelModal(v-model="showUpdateModal" title="利用者情報を更新しますか？" @ok="doUpdate")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoResidentList")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { ResidentListState, SendMessagesState } from "@/store/NeedRefreshes";

import CustomValidate from "@/commons/utils/CustomValidate";
import { PcUrl } from "@/commons/constants/PcUrl";
import ResidentsService, { InAndOutDatetime, ResidentGetModel, ResidentPatchModel } from "@/commons/services/Residents";
import { SenderType } from "@/commons/models/Resident";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import RoomsService, { RoomGetModel, RoomType, UseType } from "@/commons/services/Rooms";
import SystemError from "@/commons/models/SystemError";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormInputGender from "@/pc/components/forms/FormInputGender.vue";
import FormInputSplitDate from "@/pc/components/forms/FormInputSplitDate.vue";
import FormInputSelect from "@/pc/components/forms/FormInputSelect.vue";
import FormDatetimePicker from "@/pc/components/forms/FormDatetimePicker.vue";
import FormTimePicker from "@/pc/components/forms/FormTimePicker.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";

/**
 * 利用者編集画面コンポーネント
 */
@Component({
  $_veeValidate: { validator: "new" },
  components: {
    OkCancelModal,
    OkModal,
    PageLoading,
    ValidationAlert,
    HelpIcon,
    FormLabel,
    FormInput,
    FormInputGender,
    FormInputSplitDate,
    FormInputSelect,
    FormDatetimePicker,
    FormTimePicker
  }
})
export default class ResidentEdit extends Vue {
  /** 部屋サービス */
  @Inject("RoomsService") roomsService!: RoomsService;
  /** 利用者サービス */
  @Inject("ResidentsService") residentsService!: ResidentsService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 利用者 */
  resident: ResidentGetModel | any = {};
  /** 生年月日・年 */
  year: string = "";
  /** 生年月日・月 */
  month: string = "";
  /** 生年月日・日 */
  day: string = "";
  /** 部屋セレクトボックス用項目 */
  roomOptions: Array<{ value: number | string; label: string }> = [];
  /** グループセレクトボックス用項目 */
  residentGroupOptions: Array<{ value: number | string; label: string }> = [];
  /** 入退所情報 */
  inAndOutDatetimes: Array<ResidentEditInAndOutDatetime> = [];

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### vue lifecycle created
   */
  created() {
    CustomValidate.addCustomValidate(this.$validator);
    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    await this.createView();

    this.isBusy = false;

    return;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    const resident: ResidentGetModel = await this.getResident();
    this.resident = resident;

    // 生年月日は分割する
    if (resident.birthday) {
      this.year = this.$dateUtil.convertNumberToString(resident.birthday, "yyyy");
      this.month = this.$dateUtil.convertNumberToString(resident.birthday, "mm", false);
      this.day = this.$dateUtil.convertNumberToString(resident.birthday, "dd", false);
    }

    const rooms: Array<RoomGetModel> = await this.getRooms();
    const roomOptions: Array<{ value: number; label: string }> = this.createRoomOptions(rooms);
    this.roomOptions = roomOptions;

    const inAndOutDatetimes: Array<ResidentEditInAndOutDatetime> = [];
    // v3.29.0からv3.30.0にアップデートしたタイミングでキャッシュクリアしていない施設でinAndOutDatetimesがundefinedになる可能性がある
    // undefinedの場合はスキップする
    if (resident.inAndOutDatetimes) {
      for (const inAndOutDatetime of resident.inAndOutDatetimes) {
        let item: ResidentEditInAndOutDatetime = {
          inDatetime: null,
          inDatetimeDate: "",
          inDatetimeTime: "",
          pickUpDatetime: null,
          pickUpDatetimeTime: "",
          outDatetime: null,
          outDatetimeDate: "",
          outDatetimeTime: "",
          dropOffDatetime: null,
          dropOffDatetimeTime: "",
          roomId: null
        };

        if (inAndOutDatetime.inDatetime) {
          const inDatetimeDate: string = this.$dateUtil.convertNumberToString(inAndOutDatetime.inDatetime, "yyyy-mm-dd");
          const inDatetimeTime: string = this.$dateUtil.convertNumberToString(inAndOutDatetime.inDatetime, "hh:MM:ss");
          item.inDatetime = inAndOutDatetime.inDatetime;
          item.inDatetimeDate = inDatetimeDate;
          item.inDatetimeTime = inDatetimeTime;
        } else {
          const inDatetimeDate: string = this.$dateUtil.convertDateToString(new Date(), "yyyy-mm-dd");
          const inDatetimeTime: string = "10:00:00";
          item.inDatetimeDate = inDatetimeDate;
          item.inDatetimeTime = inDatetimeTime;
        }
        if (inAndOutDatetime.pickUpDatetime) {
          const pickUpDatetimeTime: string = this.$dateUtil.convertNumberToString(inAndOutDatetime.pickUpDatetime, "hh:MM:ss");
          item.pickUpDatetime = pickUpDatetimeTime;
          item.pickUpDatetimeTime = pickUpDatetimeTime;
        } else {
          item.pickUpDatetimeTime = "10:00:00";
        }

        if (inAndOutDatetime.outDatetime) {
          const outDatetimeDate: string = this.$dateUtil.convertNumberToString(inAndOutDatetime.outDatetime, "yyyy-mm-dd");
          const outDatetimeTime: string = this.$dateUtil.convertNumberToString(inAndOutDatetime.outDatetime, "hh:MM:ss");
          item.outDatetime = inAndOutDatetime.outDatetime;
          item.outDatetimeDate = outDatetimeDate;
          item.outDatetimeTime = outDatetimeTime;
        } else {
          const outDatetimeDate: string = this.$dateUtil.convertDateToString(new Date(), "yyyy-mm-dd");
          const outDatetimeTime: string = "10:00:00";
          item.outDatetimeDate = outDatetimeDate;
          item.outDatetimeTime = outDatetimeTime;
        }
        if (inAndOutDatetime.dropOffDatetime) {
          const dropOffDatetimeTime: string = this.$dateUtil.convertNumberToString(inAndOutDatetime.dropOffDatetime, "hh:MM:ss");
          item.dropOffDatetime = dropOffDatetimeTime;
          item.dropOffDatetimeTime = dropOffDatetimeTime;
        } else {
          item.dropOffDatetimeTime = "10:00:00";
        }

        item.roomId = inAndOutDatetime.roomId;

        inAndOutDatetimes.push(item);
      }
    }
    this.inAndOutDatetimes = inAndOutDatetimes;

    const residentGroups: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    const residentGroupOptions: Array<{ value: number; label: string }> = this.createResidentGroupOptions(residentGroups);
    this.residentGroupOptions = residentGroupOptions;

    return;
  }

  /**
   * ### 編集対象の利用者取得
   */
  public async getResident(): Promise<ResidentGetModel> {
    const response: ResidentGetModel = await this.residentsService.get(userStore, Number(this.$route.params.id));
    // 電話番号が登録されていない場合が存在してしまっているので、その場合は空文字を入れる
    response.tel = response.tel ? response.tel : "";
    return response;
  }

  /**
   * ### 部屋取得
   */
  public async getRooms(): Promise<Array<RoomGetModel>> {
    const response: Array<RoomGetModel> = await this.roomsService.gets(userStore);
    return response;
  }

  public createRoomOptions(rooms: Array<RoomGetModel>): Array<{ value: number; label: string }> {
    let roomOptions: Array<{ value: number; label: string }> = [];
    for (const room of rooms) {
      roomOptions.push({
        value: room.id,
        label: `${room.name}（${this.getUseTypeName(room.useType)}・${this.getRoomTypeName(room.roomType)}）`
      });
    }

    return roomOptions;
  }

  public getUseTypeName(useType: number): string {
    if (useType == UseType.UNMANAGED) {
      return "空床管理対象外";
    } else if (useType == UseType.NORMAL_STAY) {
      return "入居用部屋";
    } else if (useType == UseType.SHORT_STAY) {
      return "ショートステイ";
    } else {
      return "";
    }
  }

  public getRoomTypeName(roomType: number): string {
    if (roomType == RoomType.UNMANAGED) {
      return "空床管理対象外";
    } else if (roomType == RoomType.SINGLE) {
      return "個室";
    } else if (roomType == RoomType.DOUBLE) {
      return "２人部屋（夫婦部屋）";
    } else if (roomType == RoomType.MULTI_MEN) {
      return "多床室（男性）";
    } else if (roomType == RoomType.MULTI_WOMEN) {
      return "多床室（女性）";
    } else {
      return "";
    }
  }

  /**
   * ### グループセレクトボックス用項目を作成する
   *
   * - アカウントの権限に応じて選択肢をマスキングする
   */
  public createResidentGroupOptions(residentGroups: Array<ResidentGroupsGetModel>): Array<{ value: number; label: string }> {
    const residentGroupOptions: Array<{ value: number; label: string }> = [];

    const allowedGroupsMap: Map<number, number> = new Map<number, number>();
    for (const allowedGroup of userStore.loginUser.permissions.residentGroups) {
      allowedGroupsMap.set(allowedGroup, allowedGroup);
    }
    for (const group of residentGroups) {
      if (allowedGroupsMap.has(group.id)) {
        residentGroupOptions.push({
          value: group.id,
          label: group.name
        });
      }
    }

    return residentGroupOptions;
  }

  /**
   * ### 送信種別を返す
   */
  public getSenderType(senderType: SenderType): string {
    if (senderType == SenderType.LINE) {
      return "LINE";
    } else if (senderType == SenderType.MAIL) {
      return "メール";
    } else {
      return "";
    }
  }

  /**
   * ### 登録確認モーダルを表示する
   *
   * - 表示前にバリデーションチェックして、エラーならモーダルは表示しない
   */
  public async showConfirmUpdateModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    const birthdayResult: boolean = await this.validateBirthday();
    const inDateTimeValidationResult: boolean = await this.validateInDateTime();

    if (validationResult && birthdayResult && inDateTimeValidationResult) {
      this.showValidationAlert = false;
      this.showUpdateModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /** 日付の不正チェックは自前で行う */
  public async validateBirthday(): Promise<boolean> {
    this.$validator.errors.remove("date");
    const birthday: number | null = this.joinBirthday();

    if (birthday) {
      const { valid, errors } = await this.$validator.verify(birthday, "exist_date", { name: "生年月日", bails: false });
      if (!valid) {
        for (const error of errors) {
          this.$validator.errors.add({ field: "date", msg: error });
        }
      }
      return valid;
    }
    return true;
  }

  /**
   * ### 分割している生年月日を結合する
   */
  private joinBirthday(): number | null {
    if (this.year || this.month || this.day) {
      // 月と日が1桁の場合は0埋めする
      let month: string = String(this.month);
      if (month.length == 1) {
        month = `0${month}`;
      }
      // 1桁の場合は0埋めする
      let day: string = String(this.day);
      if (day.length == 1) {
        day = `0${day}`;
      }

      return Number(`${this.year}${month}${day}000000000`);
    }

    return null;
  }

  /** 入所日時・退所日時のチェックは自前で行う */
  public async validateInDateTime(): Promise<boolean> {
    for (let i = 0; i < this.inAndOutDatetimes.length; i++) {
      this.$validator.errors.remove("in-date-" + i);

      if (this.inAndOutDatetimes[i].inDatetime && this.inAndOutDatetimes[i].outDatetime) {
        const inDatetime = this.inAndOutDatetimes[i].inDatetime as number;
        const outDatetime = this.inAndOutDatetimes[i].outDatetime as number;
        if (outDatetime < inDatetime) {
          this.$validator.errors.add({ field: "in-date-" + i, msg: "入所日が退所日より未来です" });
          return false;
        }
      }
    }
    return true;
  }

  /**
   * ### 利用者とひもづく家族を更新する
   */
  public async doUpdate(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const requestBody: ResidentPatchModel = this.createRequestBody();
      await this.residentsService.patch(userStore, Number(this.$route.params.id), requestBody);

      // 利用者一覧を再読み込みさせるようstoreを更新
      let residentList: ResidentListState = needRefreshesStore.residentList;
      residentList.residentList = true;
      needRefreshesStore.setResidentList(residentList);
      // メッセージ送信の宛先が変更になるので再読み込みさせるようstoreを更新
      let sendMessageState: SendMessagesState = needRefreshesStore.sendMessage;
      sendMessageState.sendMessages = true;
      needRefreshesStore.setSendMessage(sendMessageState);

      // 更新成功ダイアログを表示する
      this.showUpdateSuccessModal = true;
    } catch (error) {
      // 利用者数の上限に達している場合、ステータスコード422が返却される
      // その場合はエラーメッセージを表示する
      // ステータスコード422以外は例外を投げなおして共通エラーハンドラに処理を委譲する
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 422) {
        this.errorMessage = "ご契約プランの利用者登録数上限に達しています。\n新規に追加する場合は設定画面からプラン変更もしくは利用者を削除してから行ってください。";
      }
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      else if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 利用者を更新する
   */
  public createRequestBody(): ResidentPatchModel {
    this.resident.name = this.$stringUtil.convertYen(this.resident.name);
    this.resident.room = this.$stringUtil.convertYen(this.resident.room);

    // 生年月日は分割されているので結合する
    const birthday: number | null = this.joinBirthday();

    const inAndOutDatetimes: Array<InAndOutDatetime> = [];
    for (const item of this.inAndOutDatetimes) {
      // 入所送迎時間、退所送迎時間は入所日と退所送迎日の日付を使用する
      let pickUpDatetime: number | null = null;
      if (item.inDatetime && item.pickUpDatetime) {
        const pickUpDate: string = this.$dateUtil.convertNumberToString(item.inDatetime, "yyyymmdd");
        const pickUpTime: string = this.$dateUtil.replaceNonNumeric(item.pickUpDatetime); // time format is hh:MM:ss
        pickUpDatetime = Number(`${pickUpDate}${pickUpTime}000`);
      }
      let dropOffDatetime: number | null = null;
      if (item.outDatetime && item.dropOffDatetime) {
        const dropOffDate: string = this.$dateUtil.convertNumberToString(item.outDatetime, "yyyymmdd");
        const dropOffTime: string = this.$dateUtil.replaceNonNumeric(item.dropOffDatetime); // time format is hh:MM:ss
        dropOffDatetime = Number(`${dropOffDate}${dropOffTime}000`);
      }

      // 入所・退所情報が空の場合がありえるので、値があればそれを配列に格納し、なければ無視する
      if (item.inDatetime || item.outDatetime || pickUpDatetime || dropOffDatetime) {
        inAndOutDatetimes.push({
          inDatetime: item.inDatetime ? item.inDatetime : null,
          pickUpDatetime: pickUpDatetime ? pickUpDatetime : null,
          outDatetime: item.outDatetime ? item.outDatetime : null,
          dropOffDatetime: dropOffDatetime ? dropOffDatetime : null,
          roomId: item.roomId ? item.roomId : this.resident.roomId
        });
      }
    }

    const requestBody: ResidentPatchModel = {
      birthday: birthday,
      gender: this.resident.gender,
      kanaName: this.resident.kanaName ? this.$stringUtil.convertYen(this.resident.kanaName) : "",
      name: this.$stringUtil.convertYen(this.resident.name),
      tel: this.resident.tel,
      roomId: this.resident.roomId,
      groupId: this.resident.groupId,
      inAndOutDatetimes: inAndOutDatetimes,
      insuredNumber: this.resident.insuredNumber ? this.resident.insuredNumber : null,
      careInsuranceInsuredNumber: this.resident.careInsuranceInsuredNumber ? this.resident.careInsuranceInsuredNumber : null,
      updated: this.resident.updated,
      deleteFlag: this.resident.deleteFlag
    };

    return requestBody;
  }

  /**
   * ### 入所・退所情報を削除する
   */
  public deleteInAndOutDatetime(index: number): void {
    this.inAndOutDatetimes.splice(index, 1);
    return;
  }

  /**
   * ### 入所・退所情報を追加する
   */
  public addInAndOutDatetime(): void {
    if (this.inAndOutDatetimes.length < 50) {
      this.inAndOutDatetimes.push({
        inDatetime: null,
        inDatetimeDate: "",
        inDatetimeTime: "",
        pickUpDatetime: null,
        pickUpDatetimeTime: "",
        outDatetime: null,
        outDatetimeDate: "",
        outDatetimeTime: "",
        dropOffDatetime: null,
        dropOffDatetimeTime: "",
        roomId: null
      });
    }
    return;
  }

  /**
   * ### 入所・退所情報の追加不可を返す
   */
  get _disableAddInAndOutDatetime(): boolean {
    if (this.inAndOutDatetimes.length < 50) {
      return false;
    } else {
      return true;
    }
  }

  /**
   * ### 利用者一覧に遷移する
   */
  public gotoResidentList(): void {
    if (Number(this.$route.params.type) == 0) {
      this.$router.push(PcUrl.ResidentList);
    } else if (Number(this.$route.params.type) == 1) {
      this.$router.push({ name: PcUrl.ResidentDetail, params: { id: `${this.$route.params.id}` } });
    } else {
      this.$router.push(PcUrl.RoomScheduleList);
    }
    return;
  }
}

export interface ResidentEditInAndOutDatetime {
  /** 入所日 */
  inDatetime: number | null;
  /** 入所日(日) */
  inDatetimeDate: string;
  /** 入所日(時) */
  inDatetimeTime: string;
  /** 入所送迎時間 */
  pickUpDatetime: string | null;
  /** 入所送迎時間(時) */
  pickUpDatetimeTime: string;
  /** 退所日 */
  outDatetime: number | null;
  /** 退所日(日) */
  outDatetimeDate: string;
  /** 退所日(時) */
  outDatetimeTime: string;
  /** 退所送迎時間 */
  dropOffDatetime: string | null;
  /** 退所送迎時間(時) */
  dropOffDatetimeTime: string;

  /** 部屋ID */
  roomId: number | null;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

// 親要素からネストしてスタイルを指定する
#resident-edit {
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;

  .delete-btn {
    font-size: 14px !important;
    width: 50px;
    height: 24px !important;
  }

  .add-btn {
    width: 70px;
  }

  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
